'use strict';

export default function useAuthModalState(initialMode) {
    const initialFormState = {
        scenario: null,
        email: '',
        lastname: '',
        firstname: '',
        thirdname: '',
        company: '',
        regNumber: '',
        vatNumber: '',
        address: '',
        website: '',
        contact: '',
        agreeCb: false,
        agreeMedia: false,
        phone: '',
        phoneCode: '',
        phoneComplete: false,
        errors: {},
        code: '',
        status: '',
        name: '',
        timeout: 0,
        isFetching: false,
        prevMode: null,
    };

    const [mode, setMode] = React.useState(initialMode || 'login');
    const [prevMode, setPrevMode] = React.useState(null);
    const [scenario, setScenario] = React.useState(initialFormState.scenario);
    const [email, setEmail] = React.useState(initialFormState.email);
    const [lastname, setLastname] = React.useState(initialFormState.lastname);
    const [firstname, setFirstname] = React.useState(initialFormState.firstname);
    const [thirdname, setThirdname] = React.useState(initialFormState.thirdname);
    const [company, setCompany] = React.useState(initialFormState.company);
    const [regNumber, setRegNumber] = React.useState(initialFormState.regNumber);
    const [vatNumber, setVatNumber] = React.useState(initialFormState.vatNumber);
    const [address, setAddress] = React.useState(initialFormState.address);
    const [website, setWebsite] = React.useState(initialFormState.website);
    const [contact, setContact] = React.useState(initialFormState.contact);
    const [agreeCb, setAgreeCb] = React.useState(initialFormState.agreeCb);
    const [agreeMedia, setAgreeMedia] = React.useState(initialFormState.agreeMedia);
    const [phone, setPhone] = React.useState(initialFormState.phone);
    const [phoneCode, setPhoneCode] = React.useState(initialFormState.phoneCode);
    const [phoneComplete, setPhoneComplete] = React.useState(initialFormState.phoneComplete);
    const [errors, setErrors] = React.useState(initialFormState.errors);
    const [code, setCode] = React.useState(initialFormState.code);
    const [status, setStatus] = React.useState(initialFormState.status);
    const [name, setName] = React.useState(initialFormState.name);
    const [timeout, setTimeoutVal] = React.useState(initialFormState.timeout);
    const [isFetching, setIsFetching] = React.useState(initialFormState.isFetching);

    const resetForm = React.useCallback(() => {
        setScenario(initialFormState.scenario);
        setEmail(initialFormState.email);
        setLastname(initialFormState.lastname);
        setFirstname(initialFormState.firstname);
        setThirdname(initialFormState.thirdname);
        setCompany(initialFormState.company);
        setRegNumber(initialFormState.regNumber);
        setVatNumber(initialFormState.vatNumber);
        setAddress(initialFormState.address);
        setWebsite(initialFormState.website);
        setContact(initialFormState.contact);
        setAgreeCb(initialFormState.agreeCb);
        setAgreeMedia(initialFormState.agreeMedia);
        setPhone(initialFormState.phone);
        setPhoneCode(initialFormState.phoneCode);
        setPhoneComplete(initialFormState.phoneComplete);
        setErrors(initialFormState.errors);
        setCode(initialFormState.code);
        setStatus(initialFormState.status);
        setName(initialFormState.name);
        setTimeoutVal(initialFormState.timeout);
        setIsFetching(initialFormState.isFetching);
        setPrevMode(null);
    }, []);

    React.useEffect(() => {
        if (initialMode) {
            setMode(initialMode);
        }
    }, [initialMode]);

    const clearErrors = React.useCallback(() => {
        setErrors({});
    }, []);

    const changeMode = React.useCallback((nextMode, nextScenario) => {
        clearErrors();
        setMode(nextMode);
        if (nextScenario) {
            setScenario(nextScenario);
        }
    }, [clearErrors]);

    const handleLogin = React.useCallback(() => {
        setIsFetching(true);
        clearErrors();

        authAction.login({ phone, phoneCode }).then(response => {
            setIsFetching(false);
            if (response.code) {
                setPrevMode('login');
                setMode('code');
                setStatus(response.code.status);
                setTimeoutVal(response.code.timeout);
                setCode(response.code.code);
            } else {
                if (response.errors) setErrors(response.errors);
                if (response.status) setStatus(response.status);
                if (response.timeout !== undefined) setTimeoutVal(response.timeout);
                if (response.code !== undefined) setCode(response.code);
                if (response.mode) setMode(response.mode);
            }
        });
    }, [phone, phoneCode, clearErrors]);

    const handleSendRegistrationRequest = React.useCallback(() => {
        setIsFetching(true);
        clearErrors();

        authAction.sendRegistrationRequest({ scenario, phone, phoneCode, email, lastname, firstname, thirdname, company, regNumber, vatNumber, address, website, contact }).then(response => {
            setIsFetching(false);

            switch (response.status) {
                case 'status_success_sent_request':
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ event: 'thank_page' });
                    setPrevMode('register');
                    setMode('success');
                    break;
                case 'status_error_validate_user':
                case 'status_error_sent_request':
                    if (response.errors) setErrors(response.errors);
                    if (response.status) setStatus(response.status);
                    break;
                default:
                    if (response.errors) setErrors(response.errors);
                    if (response.status) setStatus(response.status);
                    break;
            }
        });
    }, [scenario, phone, phoneCode, email, lastname, firstname, thirdname, company, regNumber, vatNumber, address, website, contact, clearErrors]);

    const handleRegister = React.useCallback(() => {
        setIsFetching(true);
        clearErrors();

        authAction.register({ scenario, phone, phoneCode, email, lastname, firstname, thirdname, company, regNumber, vatNumber, address, website, contact }).then(response => {
            setIsFetching(false);
            if (response.code) {
                setPrevMode('register');
                setMode('code');
                setStatus(response.code.status);
                setTimeoutVal(response.code.timeout);
                setCode(response.code.code);
            } else {
                if (response.errors) setErrors(response.errors);
                if (response.status) setStatus(response.status);
            }
        });
    }, [scenario, phone, phoneCode, email, lastname, firstname, thirdname, company, regNumber, vatNumber, address, website, contact, clearErrors]);

    const handleChangeCode = React.useCallback((e) => {
        const nextCode = e.target.value.trim();
        setCode(nextCode);
        setStatus('');

        if (nextCode.length === 4) {
            setIsFetching(true);
            const data = prevMode === 'register'
                ? { scenario, email, lastname, firstname, thirdname, company, regNumber, vatNumber, address, website, contact, phoneCode }
                : { phoneCode };

            authAction.checkCode({ type: prevMode, phone, code: nextCode, data }).then(response => {
                setIsFetching(false);
                if (response.status === 'success_login') {
                    setName(response.firstname);
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ event: 'thank_page' });
                    clearErrors();
                    setMode('success');

                    authAction.setAuthData({ token: response.token, name: `${response.firstname} ${response.lastname}`, hasCompany: response.hasCompany });

                    if (prevMode === 'login') ymReachGoal('sign_in');
                    if (prevMode === 'register') ymReachGoal('complete_registration');
                } else {
                    $('#authCodeInput').blur();
                    setCode('');
                    setTimeoutVal(response.code.timeout);
                    setStatus(response.code.status);
                }
            });
        }
    }, [prevMode, phone, phoneCode, scenario, email, lastname, firstname, thirdname, company, regNumber, vatNumber, address, website, contact, clearErrors]);

    const handleSubmitRegister = React.useCallback(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'submit_form' });
        if (scenario === 'agency') {
            handleSendRegistrationRequest();
        } else {
            handleRegister();
        }
    }, [scenario, handleSendRegistrationRequest, handleRegister]);

    return {
        mode,
        setMode,
        prevMode,
        scenario,
        setScenario,
        email,
        setEmail,
        lastname,
        setLastname,
        firstname,
        setFirstname,
        thirdname,
        setThirdname,
        company,
        setCompany,
        regNumber,
        setRegNumber,
        vatNumber,
        setVatNumber,
        address,
        setAddress,
        website,
        setWebsite,
        contact,
        setContact,
        agreeCb,
        setAgreeCb,
        agreeMedia,
        setAgreeMedia,
        phone,
        setPhone,
        phoneCode,
        setPhoneCode,
        phoneComplete,
        setPhoneComplete,
        errors,
        setErrors,
        code,
        setCode,
        status,
        setStatus,
        name,
        timeout,
        isFetching,
        resetForm,
        clearErrors,
        changeMode,
        handleLogin,
        handleRegister,
        handleSendRegistrationRequest,
        handleChangeCode,
        handleSubmitRegister,
    };
}
